import * as _ from 'lodash';
import { Formatter, FormatterEx, Column, SlickGrid } from 'slickgrid';
import { getFormatterFactory } from '.';

export type ChainingMode = 'mapReduce' | 'join' | 'join-preserve-value';

export interface ChainFormatterOptions {
  mode?: ChainingMode;
  formatters: {
    name: string;
    opts?: object;
  }[];
  separator?: string;
  formatterContext?: string; // row | cell | value
}

const DEFAULTS = {
  mode: 'mapReduce' as const,
  separator: ' ',
  formatterContext: 'row' as const,
};

function getExportValue<T>(
  formatter: FormatterEx<T>,
  row: number,
  cell: number,
  value: any,
  columnDef: Column<T>,
  data: any,
  grid: SlickGrid,
) {
  if (formatter.skipExport) {
    return;
  }

  if (formatter.exportDataFormatter) {
    return formatter.exportDataFormatter(
      row,
      cell,
      value,
      columnDef,
      data,
      grid,
    );
  }

  if (formatter.exportRawData) {
    return value;
  }

  return formatter(row, cell, value, columnDef, data, grid);
}

function getFilterValue<T>(
  formatter: FormatterEx<T>,
  row: number,
  cell: number,
  value: any,
  columnDef: Column<T>,
  data: any,
  grid: SlickGrid,
) {
  if (formatter.filterRawData) {
    return value;
  }

  if (formatter.getCellValue) {
    return formatter.getCellValue(row, cell, value, columnDef, data, grid);
  }

  return formatter(row, cell, value, columnDef, data, grid);
}

function joinChainFormatter<T>(opts: ChainFormatterOptions): FormatterEx<T> {
  const formatters: FormatterEx<T>[] = _.map(opts.formatters, (item) =>
    getFormatterFactory(item.name)(item.opts || item),
  );

  const formatter: FormatterEx<T> = (
    row,
    cell,
    value,
    columnDef,
    data,
    grid,
  ): string => {
    return _.map(formatters, (fn) =>
      fn(row, cell, value, columnDef, data, grid),
    ).join(opts.separator);
  };

  formatter.exportDataFormatter = (
    row,
    cell,
    value,
    columnDef,
    data,
    grid,
  ): string => {
    return _.reduce(
      formatters,
      (arr, fn) => {
        const result = getExportValue(
          fn,
          row,
          cell,
          value,
          columnDef,
          data,
          grid,
        );
        if (result !== null) {
          arr.push(result);
        }
        return arr;
      },
      [] as any[],
    ).join(opts.separator);
  };

  formatter.getCellValue = (row, cell, value, columnDef, data, grid) => {
    return _.reduce(
      formatters,
      (arr, fn) => {
        const result = getFilterValue(
          fn,
          row,
          cell,
          value,
          columnDef,
          data,
          grid,
        );
        if (result !== null) {
          arr.push(result);
        }
        return arr;
      },
      [] as any[],
    ).join(opts.separator);
  };

  return formatter;
}

function mapReduceFormatter<T>(opts: ChainFormatterOptions): Formatter<T> {
  const formatters: FormatterEx<T>[] = _.map(opts.formatters, (item) =>
    getFormatterFactory(item.name)(item.opts || item),
  );
  const separator = opts.separator || '';
  const formatterContext = opts.formatterContext || DEFAULTS.formatterContext;
  const useRowAsContext = formatterContext === 'row';

  const formatter: FormatterEx<T> = (
    row,
    cell,
    value,
    columnDef,
    data,
    grid,
  ) => {
    if (_.isArray(value)) {
      return _.map(value, (val) =>
        _.reduce(
          formatters,
          (m, fn) =>
            fn(row, cell, m, columnDef, useRowAsContext ? data : val, grid),
          val,
        ),
      ).join(separator);
    }

    return _.reduce(
      formatters,
      (m, fn) =>
        fn(row, cell, m, columnDef, useRowAsContext ? data : value, grid),
      value,
    );
  };

  formatter.exportDataFormatter = (
    row,
    cell,
    value,
    columnDef,
    data,
    grid,
  ): string => {
    if (_.isArray(value)) {
      return _.chain(value)
        .map((val) =>
          _.reduce(
            formatters,
            (m, fn) =>
              fn.skipExport
                ? m
                : getExportValue(fn, row, cell, m, columnDef, data, grid),
            val,
          ),
        )
        .filter((p) => p !== null)
        .value()
        .join(separator);
    }

    return _.reduce(
      formatters,
      (m, fn) =>
        fn.skipExport
          ? m
          : getExportValue(fn, row, cell, m, columnDef, data, grid),
      value,
    );
  };

  formatter.getCellValue = (row, cell, value, columnDef, data, grid) => {
    if (_.isArray(value)) {
      return _.chain(value)
        .map((val) =>
          _.reduce(
            formatters,
            (m, fn) =>
              fn.skipFilter
                ? m
                : getExportValue(fn, row, cell, m, columnDef, data, grid),
            val,
          ),
        )
        .filter((p) => p !== null)
        .value()
        .join(separator);
    }

    return _.reduce(
      formatters,
      (m, fn) =>
        fn.skipExport
          ? m
          : getExportValue(fn, row, cell, m, columnDef, data, grid),
      value,
    );
  };

  return formatter;
}

export function chain<T>(opts: ChainFormatterOptions): Formatter<T> {
  const v: ChainFormatterOptions = { ...DEFAULTS, ...opts };

  return v.mode === 'mapReduce' ? mapReduceFormatter(v) : joinChainFormatter(v);
}
